import { FunctionComponent, useState } from 'react'
import Title from '@/components/_custom/Title/title'
import { Daycare } from '@/types/Daycare'
import styles from './staffAutolink.module.scss'
import { useMutation } from '@apollo/client'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { useTranslation } from 'react-i18next'
import { Autorenew, ContentCopy, Email, PowerSettingsNew, QrCodeScanner } from '@mui/icons-material'
import { useAuth } from '@/modules/context/AuthContext'
import {
    ACTIVE_STAFF_AUTO_LINK,
    DEACTIVE_STAFF_AUTO_LINK,
    GET_STAFF_AUTO_LINK_MAIL
} from '@/modules/API/software/staffAutoLinkHandler'
import UserInput from '@/components/_custom/UserInput/userInput'
import ConfirmRenewDialog from '../Dialog/confirmRenewDialog'
import ShowQRCodeDialog from '../../../../families/list/header/Dialog/showQRCodeDialog.tsx'
import { useNotification } from '@/modules/context/NotificationContext.tsx'

interface StaffAutolinkProps {
    daycare: Daycare
}

const StaffAutolink: FunctionComponent<StaffAutolinkProps> = ({ daycare }: StaffAutolinkProps) => {
    const { t } = useTranslation()

    const { user, fetchCurrentDaycare } = useAuth()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const autoLinkPrefix: string = (import.meta.env.VITE_PUBLIC_URL || 'https://app.cald.fr') + '/?autoLink='
    const autoLinkSuffix: string = '&role=staff'

    const [autoLink, setAutoLink] = useState<string>(autoLinkPrefix + daycare.internalAutoLink + autoLinkSuffix)
    const [autoLinkActive, setAutoLinkActive] = useState<boolean>(daycare.internalAutoLinkEnable || false)

    const [mailSent, setMailSent] = useState<boolean>(false)

    const [isRenewDialogOpen, setRenewDialogOpen] = useState<boolean>(false)

    const [isQRCodeDialogOpen, setQRCodeDialogOpen] = useState<boolean>(false)

    function renewAutoLink(newAutoLink: string): void {
        fetchCurrentDaycare()
        setAutoLink(autoLinkPrefix + newAutoLink)
    }

    const [sendAutoLinkMail] = useMutation(GET_STAFF_AUTO_LINK_MAIL, {
        variables: {
            daycareId: daycare.id
        },
        onCompleted: () => {
            setMailSent(true)
            showNotificationSuccess(t('autoLinkMailSuccess'))
        },
        onError: () => {
            showNotificationError(t('autoLinkMailError'))
        }
    })

    const [deactiveStaffAutoLink] = useMutation(DEACTIVE_STAFF_AUTO_LINK, {
        variables: {
            daycareId: daycare.id
        },
        onCompleted: () => {
            fetchCurrentDaycare()

            setAutoLinkActive(false)

            showNotificationSuccess(t('autoLinkDeactivated'))
        },
        onError: () => {
            showNotificationError(t('autoLinkDeactivatedError'))
        }
    })

    const [activeStaffAutoLink] = useMutation(ACTIVE_STAFF_AUTO_LINK, {
        variables: {
            daycareId: daycare.id
        },
        onCompleted: () => {
            fetchCurrentDaycare()
            setAutoLinkActive(true)

            showNotificationSuccess(t('autoLinkActivated'))
        },
        onError: () => {
            showNotificationError(t('autoLinkActivatedError'))
        }
    })

    function copyLink(): void {
        navigator.clipboard.writeText(autoLink).then(() => {
            showNotificationSuccess(t('autoLinkCopied'))
        })
    }

    return (
        <div className={styles.AutoLinkPlacement}>
            <div className={styles.Form}>
                <div className={styles.TitleAndButton}>
                    <Title text={t('staffAutoLink')} className={styles.Title} />
                    {user?.currentDaycareRights.canWriteParamsDaycare && autoLinkActive && (
                        <ButtonTextIcon
                            type={'custom'}
                            className={styles.Button}
                            onClick={deactiveStaffAutoLink}
                            customText={t('deactive')}
                            customIcon={<PowerSettingsNew />}
                        />
                    )}
                </div>
                <div className={styles.Container}>
                    {!autoLinkActive && (
                        <div className={styles.InputContainerDesactive}>
                            <ButtonTextIcon
                                type={'custom'}
                                className={styles.Button}
                                onClick={activeStaffAutoLink}
                                customText={t('active')}
                                customIcon={<PowerSettingsNew />}
                            />
                        </div>
                    )}
                    <div className={styles.InputContainer}>
                        <UserInput
                            label={t('autoLink')}
                            value={autoLink}
                            readOnly={autoLinkActive}
                            disabled={!autoLinkActive}
                            fullWidth
                        />
                        {autoLinkActive && <ContentCopy className={styles.Icon} onClick={copyLink} />}
                    </div>
                    <div className={styles.ButtonContainer}>
                        <ButtonTextIcon
                            type={'custom'}
                            onClick={sendAutoLinkMail}
                            customText={t('receiveMail')}
                            disabled={!autoLinkActive || mailSent}
                            customIcon={<Email />}
                            className={styles.Button}
                        />
                        <ButtonTextIcon
                            type={'custom'}
                            onClick={() => setQRCodeDialogOpen(true)}
                            customText={t('showQRCode')}
                            disabled={!autoLinkActive}
                            customIcon={<QrCodeScanner />}
                            className={styles.Button}
                        />
                        {user?.currentDaycareRights.canWriteParamsDaycare && (
                            <ButtonTextIcon
                                type={'custom'}
                                className={styles.Button}
                                onClick={() => setRenewDialogOpen(true)}
                                customText={t('renew')}
                                customIcon={<Autorenew />}
                                disabled={!autoLinkActive}
                            />
                        )}
                    </div>
                </div>
            </div>
            <ShowQRCodeDialog
                open={isQRCodeDialogOpen}
                onClose={() => setQRCodeDialogOpen(false)}
                autoLink={autoLink}
                pdfVariant='staff'
                daycareName={daycare.name}
                daycareLogoBase64Data={daycare.logoBase64Data}
            />
            <ConfirmRenewDialog
                open={isRenewDialogOpen}
                staffLink={true}
                closeDialog={() => setRenewDialogOpen(false)}
                daycareId={daycare.id}
                renewAutoLink={renewAutoLink}
            />
        </div>
    )
}

export default StaffAutolink
